import type { CatalogEntry } from './catalog';

/*
 * A MARKETPLACE WIDGET BECOMING SOMETHING A BOARD CAN PLACE.
 *
 * This is the seam where bytes published by a STRANGER'S ORGANISATION turn into
 * a tile on somebody's screen, so it is written as a validator rather than a
 * parser: everything is refused unless it is recognised, and nothing is
 * repaired. A materialiser that quietly fixed a malformed bundle would be
 * guaranteeing only its own imagination, and the thing it is guarding is the
 * one surface on this platform that deliberately crosses the org boundary.
 *
 * WHAT PROTECTS THE USER IS NOT THIS. Widget source runs in an origin-isolated
 * frame with no DOM, no network and no timers, under a capability grant that
 * decides every symbol it may read. That is the boundary, it already exists,
 * and it is what makes running a stranger's code acceptable at all. This layer
 * exists for the failures the sandbox does not address: a bundle that is not a
 * widget, one carrying a binding to somebody else's plant, and one large enough
 * to be a denial of service against the board rather than an exploit of it.
 *
 * INSTALLED WIDGETS ARE NOT STORED. The source is inlined into the tile at
 * placement, exactly as a catalog widget's is — so a board carries its widgets
 * rather than pointing at a catalog that can change underneath it, and an
 * installed widget cannot be silently updated beneath a running screen. That is
 * clone-to-own applied one level down, and it is why nothing here writes.
 */

/** What a published widget artifact is, decoded. Deliberately the catalog's own
 *  shape minus its id: a marketplace widget is placed by exactly the same code
 *  that places a built-in one, and a second shape would be a second code path. */
export interface WidgetArtifact {
  title: string;
  summary: string;
  configKeys: string[];
  defaults: Record<string, unknown>;
  minW: number;
  minH: number;
  source: string;
}

export type Materialised = { ok: true; entry: CatalogEntry } | { ok: false; reason: string };

/*
 * Bounds. Each is a number somebody could argue with; what matters is that a
 * bundle past them is REFUSED rather than placed and discovered later.
 *
 * Source is capped because it is compiled and run per tile: a board with fifty
 * copies of a megabyte widget is a page that never becomes interactive, and no
 * legitimate widget is close — the largest in the built-in catalog is ~2 KB.
 */
const MAX_SOURCE_BYTES = 256 * 1024;
const MAX_CONFIG_KEYS = 32;
const MAX_TEXT = 200;

/*
 * Config keys a widget may NOT declare defaults for.
 *
 * `controllerId` and `symbolId` are the installer's to fill — that is the entire
 * point of publishing an artifact with open slots. A default for either would
 * be the publisher's plant travelling inside a bundle that passed the strip,
 * and it would bind a stranger's board to a controller id that means nothing on
 * this floor. The publish validator refuses these on the way out; this refuses
 * them on the way in, because a validator on one side of a boundary is a
 * validator somebody can route around.
 */
const INSTALLER_OWNED = new Set(['controllerId', 'controller_id', 'symbolId', 'symbol_id', 'symbolIds', 'symbol_ids', 'gatewayId', 'gateway_id']);

const text = (v: unknown): string => (typeof v === 'string' ? v : '');

/**
 * Turn a decoded bundle into a placeable catalog entry, or say why not.
 *
 * `itemId` becomes the entry's id, prefixed, so a marketplace widget can never
 * shadow a built-in one: an artifact named `value` must not become the widget a
 * board gets when its author asked for the platform's `value`.
 */
export function materialiseWidget(itemId: string, decoded: unknown): Materialised {
  if (!decoded || typeof decoded !== 'object' || Array.isArray(decoded)) {
    return { ok: false, reason: 'That item is not a widget — its contents are not a widget definition.' };
  }
  const a = decoded as Partial<WidgetArtifact>;

  const source = text(a.source);
  if (!source.trim()) {
    return { ok: false, reason: 'That widget has no source, so there is nothing to render.' };
  }
  if (source.length > MAX_SOURCE_BYTES) {
    return {
      ok: false,
      reason: `That widget is ${Math.round(source.length / 1024)} KB; the limit is ${MAX_SOURCE_BYTES / 1024} KB. A widget is a drawing function, not a payload.`,
    };
  }

  const title = text(a.title).trim();
  if (!title) return { ok: false, reason: 'That widget has no title, so nothing could label it on a board.' };
  if (title.length > MAX_TEXT || text(a.summary).length > MAX_TEXT) {
    return { ok: false, reason: 'That widget’s title or summary is too long to show on a card.' };
  }

  const configKeys = Array.isArray(a.configKeys) ? a.configKeys.filter((k): k is string => typeof k === 'string') : [];
  if (configKeys.length > MAX_CONFIG_KEYS) {
    return { ok: false, reason: `That widget declares ${configKeys.length} settings; ${MAX_CONFIG_KEYS} is the limit.` };
  }

  const rawDefaults = a.defaults && typeof a.defaults === 'object' && !Array.isArray(a.defaults) ? (a.defaults as Record<string, unknown>) : {};
  for (const key of Object.keys(rawDefaults)) {
    if (INSTALLER_OWNED.has(key)) {
      // Named rather than stripped. Stripping would install a widget whose
      // author believed it was bound, and the tile would render a placeholder
      // with nobody able to say why.
      return {
        ok: false,
        reason: `That widget ships a default for "${key}", which is a binding to the publisher’s own plant. It cannot be installed; the publisher needs to remove it.`,
      };
    }
  }

  return {
    ok: true,
    entry: {
      // PREFIXED so an installed widget can never shadow a built-in one.
      id: `marketplace:${itemId}`,
      title,
      summary: text(a.summary).trim(),
      configKeys,
      defaults: rawDefaults,
      // Clamped rather than trusted: a zero or a negative would collapse the
      // tile, and an enormous one would take the grid.
      minW: bounded(a.minW, 1, 12, 2),
      minH: bounded(a.minH, 1, 12, 2),
      source,
    },
  };
}

function bounded(v: unknown, lo: number, hi: number, fallback: number): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(Math.max(n, lo), hi);
}

/** True for a widget id that names a marketplace item rather than a built-in. */
export const isMarketplaceWidget = (id: string): boolean => id.startsWith('marketplace:');

/** The item id inside a marketplace widget id. */
export const marketplaceItemId = (id: string): string => id.slice('marketplace:'.length);

/**
 * gunzip + JSON.parse, the shape the marketplace publishes.
 *
 * Returns undefined rather than throwing for anything unreadable: a bundle that
 * cannot be decoded and one that decodes to nonsense are the same answer to a
 * caller, and both are refused by the materialiser above.
 */
export async function decodeWidgetBundle(bundle: Uint8Array): Promise<unknown | undefined> {
  if (bundle.length < 2 || bundle[0] !== 0x1f || bundle[1] !== 0x8b) return undefined;
  try {
    const stream = new Response(bundle as unknown as BodyInit).body?.pipeThrough(new DecompressionStream('gzip'));
    if (!stream) return undefined;
    return JSON.parse(await new Response(stream).text());
  } catch {
    return undefined;
  }
}
